import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle, AlertTriangle, XCircle, SkipForward, Loader2,
  Brain, BookOpen, Scale, ExternalLink, ChevronRight,
  Sparkles, Clock, FileCheck, Flag, Keyboard, RefreshCw,
} from "lucide-react";
import { useReviewQueue, type ReviewMcq } from "@/hooks/useReviewQueue";
import { useReviewKeyboardShortcuts, type Shortcut } from "@/hooks/useReviewKeyboardShortcuts";
import { ReviewFiltersBar, AiRiskPill } from "./ReviewFiltersBar";
import { ShortcutsCheatSheet } from "./ShortcutsCheatSheet";
import {
  AISuggestionsDiff,
  type FieldSuggestion,
  type SuggestionField,
} from "./AISuggestionsDiff";
import { QuickAIActions } from "./QuickAIActions";

interface AIVerification {
  clinical_expert: any;
  evidence_reviewer: any;
  consensus: {
    verdict: string;
    confidence: number;
    summary: string;
    critical_issues: string[];
    minor_issues: string[];
    agents_agree: boolean;
    recommended_action: string;
  };
  from_cache?: boolean;
}

interface ReviewStats {
  pending: number;
  approved: number;
  flagged: number;
  total_reviewed: number;
}

const sb = supabase as any;

const MCQReviewPlatform = () => {
  const { toast } = useToast();
  const queue = useReviewQueue();
  const { items: mcqs, selected: selectedMcq, loading } = queue;

  const [stats, setStats] = useState<ReviewStats>({ pending: 0, approved: 0, flagged: 0, total_reviewed: 0 });
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState<AIVerification | null>(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editedMcq, setEditedMcq] = useState<Partial<ReviewMcq>>({});
  const [actionLoading, setActionLoading] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [autoRunAI, setAutoRunAI] = useState(true);
  const [activeTab, setActiveTab] = useState<"clinical" | "evidence" | "consensus">("clinical");
  const [aiSuggestions, setAiSuggestions] = useState<FieldSuggestion[]>([]);
  const [appliedFields, setAppliedFields] = useState<Set<SuggestionField>>(new Set());
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  // Reset per-MCQ state when selection changes
  useEffect(() => {
    setAiResult(null);
    setReviewNotes("");
    setEditMode(false);
    setEditedMcq({});
    setAiSuggestions([]);
    setAppliedFields(new Set());
  }, [selectedMcq?.id]);

  const fetchStats = useCallback(async () => {
    const [pendingRes, approvedRes, flaggedRes, reviewedRes] = await Promise.all([
      sb.from("mcqs").select("id", { count: "exact", head: true }).eq("review_status", "pending"),
      sb.from("mcqs").select("id", { count: "exact", head: true }).eq("review_status", "approved"),
      sb.from("mcqs").select("id", { count: "exact", head: true }).eq("review_status", "needs_revision"),
      sb.from("mcq_review_actions").select("id", { count: "exact", head: true }),
    ]);
    setStats({
      pending: pendingRes.count || 0,
      approved: approvedRes.count || 0,
      flagged: flaggedRes.count || 0,
      total_reviewed: reviewedRes.count || 0,
    });
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const runAIVerification = useCallback(async (force = false) => {
    if (!selectedMcq) return;
    setAiLoading(true);
    try {
      const topicName = selectedMcq.topics?.name || null;
      const { data, error } = await supabase.functions.invoke("ai-mcq-review-panel", {
        body: {
          mcq: {
            id: selectedMcq.id,
            stem: selectedMcq.stem,
            option_a: selectedMcq.option_a,
            option_b: selectedMcq.option_b,
            option_c: selectedMcq.option_c,
            option_d: selectedMcq.option_d,
            option_e: selectedMcq.option_e,
            correct_option: selectedMcq.correct_option,
            explanation: selectedMcq.explanation,
            tags: selectedMcq.tags,
            difficulty: selectedMcq.difficulty,
            topic_name: topicName,
          },
          force,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setAiResult(data);

      // Convert clinical-expert improvement suggestions into field-level diffs.
      const improvements: string[] = data?.clinical_expert?.suggested_improvements || [];
      // We can't reliably parse field-level rewrites from the verification panel,
      // so we leave the diff list empty here — admins can use Quick AI per field
      // to generate concrete diffs.
      void improvements;
    } catch (e: any) {
      toast({ title: "AI Verification Failed", description: e.message, variant: "destructive" });
    } finally {
      setAiLoading(false);
    }
  }, [selectedMcq, toast]);

  // Auto-run AI when a new MCQ is opened.
  useEffect(() => {
    if (!autoRunAI || !selectedMcq || aiLoading || aiResult) return;
    runAIVerification(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMcq?.id, autoRunAI]);

  const handleAction = useCallback(async (action: "approve" | "flag" | "correct" | "skip") => {
    if (!selectedMcq) return;
    setActionLoading(true);
    try {
      const reviewStatusMap: Record<string, string> = {
        approve: "approved",
        flag: "needs_revision",
        correct: "approved",
        skip: selectedMcq.review_status || "pending",
      };

      if (action === "correct" && (editMode || appliedFields.size > 0)) {
        const updates: any = {};
        if (editedMcq.stem) updates.stem = editedMcq.stem;
        if (editedMcq.option_a) updates.option_a = editedMcq.option_a;
        if (editedMcq.option_b) updates.option_b = editedMcq.option_b;
        if (editedMcq.option_c) updates.option_c = editedMcq.option_c;
        if (editedMcq.option_d !== undefined) updates.option_d = editedMcq.option_d;
        if (editedMcq.option_e !== undefined) updates.option_e = editedMcq.option_e;
        if (editedMcq.explanation) updates.explanation = editedMcq.explanation;
        updates.review_status = "approved";
        updates.last_reviewed_at = new Date().toISOString();
        const { error } = await sb.from("mcqs").update(updates).eq("id", selectedMcq.id);
        if (error) throw error;
      } else if (action !== "skip") {
        const { error } = await sb.from("mcqs").update({
          review_status: reviewStatusMap[action],
          last_reviewed_at: new Date().toISOString(),
        }).eq("id", selectedMcq.id);
        if (error) throw error;
      }

      if (action !== "skip") {
        const { data: { user } } = await supabase.auth.getUser();
        await sb.from("mcq_review_actions").insert({
          mcq_id: selectedMcq.id,
          reviewer_id: user!.id,
          action,
          ai_verification: aiResult as any,
          notes: reviewNotes || null,
          corrections: action === "correct" ? (editedMcq as any) : null,
        });
      }

      toast({
        title: action === "skip" ? "Skipped" : `MCQ ${action === "approve" ? "approved" : action === "flag" ? "flagged" : "corrected"}`,
        description: autoAdvance ? "Moving to next." : "Review action recorded.",
      });

      fetchStats();
      if (autoAdvance && action !== "skip") {
        queue.removeAndAdvance(selectedMcq.id);
      } else if (action === "skip") {
        queue.next();
      } else {
        queue.reload();
      }
    } catch (e: any) {
      toast({ title: "Action failed", description: e.message, variant: "destructive" });
    } finally {
      setActionLoading(false);
    }
  }, [selectedMcq, editMode, editedMcq, appliedFields.size, aiResult, reviewNotes, autoAdvance, queue, toast, fetchStats]);

  // Keyboard shortcuts
  const shortcuts: Shortcut[] = useMemo(() => ([
    { key: "j", description: "Next MCQ", group: "Navigation", handler: () => queue.next() },
    { key: "arrowdown", handler: () => queue.next() },
    { key: "k", description: "Previous MCQ", group: "Navigation", handler: () => queue.prev() },
    { key: "arrowup", handler: () => queue.prev() },
    { key: "a", description: "Approve", group: "Actions", handler: () => !actionLoading && handleAction("approve") },
    { key: "f", description: "Flag", group: "Actions", handler: () => !actionLoading && handleAction("flag") },
    { key: "s", description: "Skip", group: "Actions", handler: () => !actionLoading && handleAction("skip") },
    { key: "e", description: "Toggle edit mode", group: "Actions", handler: () => setEditMode((v) => !v) },
    { key: " ", description: "Run AI verification", group: "AI", handler: (e) => { e.preventDefault(); runAIVerification(false); } },
    { key: "1", description: "Clinical tab", group: "AI", handler: () => setActiveTab("clinical") },
    { key: "2", description: "Evidence tab", group: "AI", handler: () => setActiveTab("evidence") },
    { key: "3", description: "Consensus tab", group: "AI", handler: () => setActiveTab("consensus") },
    { key: "escape", description: "Exit edit mode", group: "Actions", handler: () => setEditMode(false) },
    { key: "?", description: "Show shortcuts", group: "Help", handler: () => setShortcutsOpen(true) },
  ]), [queue, actionLoading, handleAction, runAIVerification]);

  useReviewKeyboardShortcuts(shortcuts);

  const cheatSheetItems = shortcuts
    .filter((s) => s.description)
    .map((s) => ({ keys: [s.key === " " ? "Space" : s.key.toUpperCase()], description: s.description!, group: s.group }));

  const getVerdictColor = (verdict: string) => {
    switch (verdict) {
      case "approve": return "text-success bg-success/10 border-success/30";
      case "flag": return "text-amber-600 bg-amber-500/10 border-amber-500/30";
      case "needs_correction": return "text-destructive bg-destructive/10 border-destructive/30";
      default: return "text-muted-foreground bg-muted";
    }
  };
  const getScoreColor = (score: number) => score >= 80 ? "text-success" : score >= 60 ? "text-amber-600" : "text-destructive";

  const handleApplySuggestion = (field: SuggestionField, value: string) => {
    setEditedMcq((p) => ({ ...p, [field]: value }));
    setAppliedFields((prev) => new Set(prev).add(field));
    setEditMode(true);
  };
  const handleRejectSuggestion = (field: SuggestionField) => {
    setAppliedFields((prev) => {
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
    setEditedMcq((p) => {
      const next = { ...p };
      delete (next as any)[field];
      return next;
    });
    setAiSuggestions((prev) => prev.filter((s) => s.field !== field));
  };
  const handleApplyAll = () => {
    aiSuggestions.forEach((s) => handleApplySuggestion(s.field, s.suggested));
  };

  const onQuickSuggestion = (s: { field: SuggestionField; suggested: string; rationale: string }) => {
    if (!selectedMcq) return;
    const current = (selectedMcq[s.field as keyof ReviewMcq] as string) || "";
    setAiSuggestions((prev) => {
      const without = prev.filter((p) => p.field !== s.field);
      return [...without, { field: s.field, current, suggested: s.suggested, rationale: s.rationale }];
    });
  };

  const mcqContext = selectedMcq && {
    stem: selectedMcq.stem,
    option_a: selectedMcq.option_a,
    option_b: selectedMcq.option_b,
    option_c: selectedMcq.option_c,
    option_d: selectedMcq.option_d,
    option_e: selectedMcq.option_e,
    correct_option: selectedMcq.correct_option,
    explanation: selectedMcq.explanation,
    topic_name: selectedMcq.topics?.name || null,
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatTile icon={Clock} label="Pending" value={stats.pending} tone="amber" />
        <StatTile icon={CheckCircle} label="Approved" value={stats.approved} tone="green" />
        <StatTile icon={Flag} label="Flagged" value={stats.flagged} tone="red" />
        <StatTile icon={FileCheck} label="Total reviewed" value={stats.total_reviewed} tone="blue" />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <ReviewFiltersBar
            filters={queue.filters}
            onFiltersChange={queue.setFilters}
            sort={queue.sort}
            onSortChange={queue.setSort}
            autoAdvance={autoAdvance}
            onAutoAdvanceChange={setAutoAdvance}
            autoRunAI={autoRunAI}
            onAutoRunAIChange={setAutoRunAI}
          />
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{mcqs.length} in queue • {queue.currentIndex >= 0 ? `${queue.currentIndex + 1} of ${mcqs.length}` : "—"}</span>
            <Button variant="ghost" size="sm" onClick={() => setShortcutsOpen(true)} className="h-7">
              <Keyboard className="h-3.5 w-3.5 mr-1" /> Shortcuts (?)
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Queue List */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Review queue</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[600px]">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : mcqs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No MCQs match these filters</p>
              ) : (
                mcqs.map((mcq) => (
                  <button
                    key={mcq.id}
                    onClick={() => queue.setSelectedId(mcq.id)}
                    className={`w-full text-left p-3 border-b hover:bg-accent/50 transition-colors ${
                      selectedMcq?.id === mcq.id ? "bg-accent" : ""
                    }`}
                  >
                    <p className="text-sm font-medium line-clamp-2">{mcq.stem}</p>
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      <Badge variant={mcq.review_status === "approved" ? "default" : mcq.review_status === "needs_revision" ? "destructive" : "secondary"} className="text-[10px] px-1.5 py-0">
                        {mcq.review_status || "pending"}
                      </Badge>
                      {mcq.ai_risk && (
                        <AiRiskPill verdict={mcq.ai_risk.verdict} confidence={mcq.ai_risk.confidence} />
                      )}
                      {mcq.topics?.name && (
                        <span className="text-[10px] text-muted-foreground truncate">{mcq.topics.name}</span>
                      )}
                    </div>
                  </button>
                ))
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Main */}
        <div className="lg:col-span-9 space-y-4">
          {selectedMcq ? (
            <>
              {/* MCQ Card */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-base">Question review</CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">Difficulty: {selectedMcq.difficulty || "N/A"}</Badge>
                      {selectedMcq.topics?.name && (
                        <Badge variant="secondary">{selectedMcq.topics.name}</Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {editMode ? (
                    <div className="space-y-3">
                      <FieldEditor
                        label="Stem"
                        field="stem"
                        value={editedMcq.stem ?? selectedMcq.stem}
                        onChange={(v) => setEditedMcq((p) => ({ ...p, stem: v }))}
                        multiline
                        mcqId={selectedMcq.id}
                        mcqContext={mcqContext!}
                        onSuggestion={onQuickSuggestion}
                      />
                      {(["option_a", "option_b", "option_c", "option_d", "option_e"] as const).map((key) => {
                        const cur = (editedMcq as any)[key] ?? (selectedMcq as any)[key] ?? "";
                        if (key === "option_e" && !cur && !selectedMcq.option_e) return null;
                        return (
                          <FieldEditor
                            key={key}
                            label={key.replace("_", " ").toUpperCase()}
                            field={key}
                            value={cur}
                            onChange={(v) => setEditedMcq((p) => ({ ...p, [key]: v }))}
                            mcqId={selectedMcq.id}
                            mcqContext={mcqContext!}
                            onSuggestion={onQuickSuggestion}
                          />
                        );
                      })}
                      <FieldEditor
                        label="Explanation"
                        field="explanation"
                        value={editedMcq.explanation ?? selectedMcq.explanation ?? ""}
                        onChange={(v) => setEditedMcq((p) => ({ ...p, explanation: v }))}
                        multiline
                        mcqId={selectedMcq.id}
                        mcqContext={mcqContext!}
                        onSuggestion={onQuickSuggestion}
                      />
                    </div>
                  ) : (
                    <>
                      <p className="text-sm font-medium whitespace-pre-wrap">{selectedMcq.stem}</p>
                      <div className="space-y-2">
                        {(["A", "B", "C", "D", "E"] as const).map((letter) => {
                          const key = `option_${letter.toLowerCase()}` as keyof ReviewMcq;
                          const val = selectedMcq[key];
                          if (!val) return null;
                          const isCorrect = selectedMcq.correct_option.toUpperCase() === letter;
                          return (
                            <div
                              key={letter}
                              className={`p-2.5 rounded-md border text-sm ${
                                isCorrect ? "border-success/40 bg-success/5 text-foreground" : "border-border"
                              }`}
                            >
                              <span className="font-semibold mr-2">{letter}.</span>
                              {val as string}
                              {isCorrect && <CheckCircle className="inline-block ml-2 h-3.5 w-3.5 text-success" />}
                            </div>
                          );
                        })}
                      </div>
                      {selectedMcq.explanation && (
                        <div className="bg-muted/50 rounded-md p-3">
                          <p className="text-xs font-medium text-muted-foreground mb-1">Explanation</p>
                          <p className="text-sm whitespace-pre-wrap">{selectedMcq.explanation}</p>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* AI suggestions diff */}
              {aiSuggestions.length > 0 && (
                <AISuggestionsDiff
                  suggestions={aiSuggestions}
                  appliedFields={appliedFields}
                  onApply={handleApplySuggestion}
                  onReject={handleRejectSuggestion}
                  onApplyAll={handleApplyAll}
                />
              )}

              {/* AI Verification Panel */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-primary" />
                      3-agent AI verification
                      {aiResult?.from_cache && (
                        <Badge variant="outline" className="text-[10px]">cached</Badge>
                      )}
                    </CardTitle>
                    <div className="flex items-center gap-1.5">
                      {aiResult && (
                        <Button onClick={() => runAIVerification(true)} disabled={aiLoading} size="sm" variant="ghost">
                          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Re-run
                        </Button>
                      )}
                      <Button onClick={() => runAIVerification(false)} disabled={aiLoading} size="sm">
                        {aiLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Brain className="h-4 w-4 mr-1" />}
                        {aiLoading ? "Analyzing..." : aiResult ? "Re-check" : "Run verification"}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {aiLoading && !aiResult && (
                    <div className="flex flex-col items-center justify-center py-8 gap-3">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      <p className="text-sm text-muted-foreground">Running 3-agent verification…</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Brain className="h-3 w-3" /> Clinical</span>
                        <ChevronRight className="h-3 w-3" />
                        <span className="flex items-center gap-1"><BookOpen className="h-3 w-3" /> Evidence</span>
                        <ChevronRight className="h-3 w-3" />
                        <span className="flex items-center gap-1"><Scale className="h-3 w-3" /> Consensus</span>
                      </div>
                    </div>
                  )}

                  {!aiLoading && !aiResult && (
                    <p className="text-sm text-muted-foreground text-center py-6">
                      Press <kbd className="px-1 py-0.5 rounded border text-xs">Space</kbd> or click Run.
                    </p>
                  )}

                  {aiResult && (
                    <div className="space-y-4">
                      <div className={`rounded-lg border p-4 ${getVerdictColor(aiResult.consensus.verdict)}`}>
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                          <div className="flex items-center gap-2">
                            <Scale className="h-5 w-5" />
                            <span className="font-semibold text-sm">
                              Verdict: {aiResult.consensus.verdict.replace("_", " ").toUpperCase()}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs">Confidence:</span>
                            <span className="font-bold">{aiResult.consensus.confidence}%</span>
                            {aiResult.consensus.agents_agree && (
                              <Badge variant="outline" className="text-[10px]">Agents agree</Badge>
                            )}
                          </div>
                        </div>
                        <p className="text-sm">{aiResult.consensus.summary}</p>
                        {aiResult.consensus.recommended_action && (
                          <p className="text-xs mt-2 opacity-80">
                            <strong>Recommended:</strong> {aiResult.consensus.recommended_action}
                          </p>
                        )}
                      </div>

                      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
                        <TabsList className="w-full">
                          <TabsTrigger value="clinical" className="flex-1 text-xs">
                            <Brain className="h-3 w-3 mr-1" /> Clinical
                          </TabsTrigger>
                          <TabsTrigger value="evidence" className="flex-1 text-xs">
                            <BookOpen className="h-3 w-3 mr-1" /> Evidence
                          </TabsTrigger>
                          <TabsTrigger value="consensus" className="flex-1 text-xs">
                            <Scale className="h-3 w-3 mr-1" /> Consensus
                          </TabsTrigger>
                        </TabsList>

                        <TabsContent value="clinical" className="space-y-3 mt-3">
                          <div className="grid grid-cols-3 gap-3">
                            <ScoreTile label="Accuracy" value={aiResult.clinical_expert.accuracy_score} colorFn={getScoreColor} />
                            <div className="text-center p-3 rounded-lg bg-muted/50">
                              {aiResult.clinical_expert.correct_answer_valid ? (
                                <CheckCircle className="h-6 w-6 text-success mx-auto" />
                              ) : (
                                <XCircle className="h-6 w-6 text-destructive mx-auto" />
                              )}
                              <p className="text-[10px] text-muted-foreground mt-1">Answer valid</p>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-muted/50">
                              <Badge>{aiResult.clinical_expert.explanation_quality}</Badge>
                              <p className="text-[10px] text-muted-foreground mt-1">Explanation</p>
                            </div>
                          </div>
                          {aiResult.clinical_expert.clinical_notes && (
                            <p className="text-sm text-muted-foreground">{aiResult.clinical_expert.clinical_notes}</p>
                          )}
                          <IssueList title="Stem issues" items={aiResult.clinical_expert.stem_issues} icon={AlertTriangle} tone="amber" />
                          {aiResult.clinical_expert.option_issues?.length > 0 && (
                            <div>
                              <p className="text-xs font-medium mb-1 text-destructive">Option issues</p>
                              <ul className="space-y-1">
                                {aiResult.clinical_expert.option_issues.map((oi: any, i: number) => (
                                  <li key={i} className="text-sm text-muted-foreground">
                                    <span className="font-medium">{oi.option}:</span> {oi.issue}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <IssueList title="Suggested improvements" items={aiResult.clinical_expert.suggested_improvements} icon={Sparkles} tone="primary" />
                        </TabsContent>

                        <TabsContent value="evidence" className="space-y-3 mt-3">
                          <div className="grid grid-cols-3 gap-3">
                            <ScoreTile label="Evidence" value={aiResult.evidence_reviewer.evidence_score} colorFn={getScoreColor} />
                            <div className="text-center p-3 rounded-lg bg-muted/50">
                              <Badge>{aiResult.evidence_reviewer.evidence_support}</Badge>
                              <p className="text-[10px] text-muted-foreground mt-1">Support</p>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-muted/50">
                              <Badge>{aiResult.evidence_reviewer.guideline_compliance?.replace("_", " ")}</Badge>
                              <p className="text-[10px] text-muted-foreground mt-1">Guidelines</p>
                            </div>
                          </div>
                          {aiResult.evidence_reviewer.evidence_notes && (
                            <p className="text-sm text-muted-foreground">{aiResult.evidence_reviewer.evidence_notes}</p>
                          )}
                          {aiResult.evidence_reviewer.pubmed_citations?.length > 0 && (
                            <div className="space-y-2">
                              <p className="text-xs font-medium">PubMed citations</p>
                              {aiResult.evidence_reviewer.pubmed_citations.map((c: any) => (
                                <a key={c.pmid} href={c.url} target="_blank" rel="noopener noreferrer"
                                   className="block p-2 rounded border hover:border-primary/50 transition-colors group">
                                  <p className="text-xs font-medium group-hover:text-primary line-clamp-2 flex items-start gap-1">
                                    {c.title} <ExternalLink className="h-3 w-3 shrink-0 mt-0.5" />
                                  </p>
                                  <p className="text-[10px] text-muted-foreground mt-0.5">
                                    {c.authors} • {c.journal} ({c.year}) • PMID: {c.pmid}
                                  </p>
                                </a>
                              ))}
                            </div>
                          )}
                        </TabsContent>

                        <TabsContent value="consensus" className="space-y-3 mt-3">
                          <div>
                            <p className="text-xs font-medium mb-1">Confidence</p>
                            <div className="flex items-center gap-3">
                              <Progress value={aiResult.consensus.confidence} className="flex-1" />
                              <span className={`text-sm font-bold ${getScoreColor(aiResult.consensus.confidence)}`}>
                                {aiResult.consensus.confidence}%
                              </span>
                            </div>
                          </div>
                          <IssueList title="Critical issues" items={aiResult.consensus.critical_issues} icon={XCircle} tone="red" />
                          <IssueList title="Minor issues" items={aiResult.consensus.minor_issues} icon={AlertTriangle} tone="amber" />
                        </TabsContent>
                      </Tabs>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Action Bar */}
              <Card>
                <CardContent className="p-4 space-y-3">
                  <Textarea
                    placeholder="Add review notes (optional)…"
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    className="min-h-[60px]"
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button onClick={() => handleAction("approve")} disabled={actionLoading} className="bg-success text-success-foreground hover:opacity-90">
                      <CheckCircle className="h-4 w-4 mr-1" /> Approve <kbd className="ml-2 text-[10px] opacity-70">A</kbd>
                    </Button>
                    <Button onClick={() => handleAction("flag")} disabled={actionLoading} variant="destructive">
                      <Flag className="h-4 w-4 mr-1" /> Flag <kbd className="ml-2 text-[10px] opacity-70">F</kbd>
                    </Button>
                    <Button
                      onClick={() => editMode || appliedFields.size > 0 ? handleAction("correct") : (setEditMode(true), setEditedMcq({}))}
                      disabled={actionLoading}
                      variant="outline"
                    >
                      {editMode || appliedFields.size > 0 ? (
                        <><CheckCircle className="h-4 w-4 mr-1" /> Save corrections</>
                      ) : (
                        <><AlertTriangle className="h-4 w-4 mr-1" /> Edit <kbd className="ml-2 text-[10px] opacity-70">E</kbd></>
                      )}
                    </Button>
                    {editMode && (
                      <Button onClick={() => { setEditMode(false); setEditedMcq({}); }} variant="ghost" size="sm">
                        Cancel
                      </Button>
                    )}
                    <Button onClick={() => handleAction("skip")} disabled={actionLoading} variant="ghost" className="ml-auto">
                      <SkipForward className="h-4 w-4 mr-1" /> Skip <kbd className="ml-2 text-[10px] opacity-70">S</kbd>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <FileCheck className="h-12 w-12 text-muted-foreground mb-3" />
                <p className="text-lg font-medium">No MCQ selected</p>
                <p className="text-sm text-muted-foreground">Adjust filters or select a question from the queue.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <ShortcutsCheatSheet open={shortcutsOpen} onOpenChange={setShortcutsOpen} items={cheatSheetItems} />
    </div>
  );
};

/* ---------- Helper components ---------- */

function StatTile({ icon: Icon, label, value, tone }: { icon: any; label: string; value: number; tone: "amber" | "green" | "red" | "blue" }) {
  const toneClass = {
    amber: "bg-amber-500/15 text-amber-600",
    green: "bg-success/15 text-success",
    red: "bg-destructive/15 text-destructive",
    blue: "bg-primary/15 text-primary",
  }[tone];
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2 rounded-lg ${toneClass}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function ScoreTile({ label, value, colorFn }: { label: string; value: number; colorFn: (n: number) => string }) {
  return (
    <div className="text-center p-3 rounded-lg bg-muted/50">
      <p className={`text-2xl font-bold ${colorFn(value)}`}>{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  );
}

function IssueList({ title, items, icon: Icon, tone }: { title: string; items?: string[]; icon: any; tone: "amber" | "red" | "primary" }) {
  if (!items || items.length === 0) return null;
  const toneClass = tone === "amber" ? "text-amber-600" : tone === "red" ? "text-destructive" : "text-primary";
  return (
    <div>
      <p className={`text-xs font-medium mb-1 ${toneClass}`}>{title}</p>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-muted-foreground flex items-start gap-1.5">
            <Icon className={`h-3 w-3 mt-0.5 shrink-0 ${toneClass}`} /> {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

interface FieldEditorProps {
  label: string;
  field: SuggestionField;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  mcqId: string;
  mcqContext: any;
  onSuggestion: (s: { field: SuggestionField; suggested: string; rationale: string }) => void;
}
function FieldEditor({ label, field, value, onChange, multiline, mcqId, mcqContext, onSuggestion }: FieldEditorProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <label className="text-xs font-medium text-muted-foreground">{label}</label>
        <QuickAIActions
          field={field}
          currentValue={value}
          mcqId={mcqId}
          mcqContext={mcqContext}
          onSuggestion={onSuggestion}
        />
      </div>
      {multiline ? (
        <Textarea value={value} onChange={(e) => onChange(e.target.value)} className="min-h-[80px]" />
      ) : (
        <Input value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

export default MCQReviewPlatform;
